// Comprehensive HeatMapGrid Test Data
// This file contains all edge cases for heatmap grid testing

export const heatMapGridTestData = {
  // Basic comprehensive test data with mixed label lengths
  comprehensive: {
    name: "Comprehensive HeatMapGrid Test Data",
    description: "Mixed label lengths and various values for heatmap testing",
    data: [
      {
        display: "A",
        stack1: 25, stack2: 15, stack3: 30, stack4: 20, stack5: 10
      },
      {
        display: "Very Long Category Name That Should Test Text Wrapping and Overflow Handling in HeatMap Grid Legends",
        stack1: 15, stack2: 25, stack3: 10, stack4: 35, stack5: 5
      },
      {
        display: "Medium Length Category",
        stack1: 20, stack2: 30, stack3: 15, stack4: 25, stack5: 10
      },
      {
        display: "Short",
        stack1: 10, stack2: 20, stack3: 25, stack4: 15, stack5: 30
      },
      {
        display: "Another Very Long Category Name That Exceeds Normal Display Limits and Tests UI Responsiveness",
        stack1: 8, stack2: 12, stack3: 18, stack4: 22, stack5: 40
      },
      {
        display: "B",
        stack1: 12, stack2: 18, stack3: 22, stack4: 8, stack5: 40
      },
      {
        display: "Normal Category",
        stack1: 18, stack2: 22, stack3: 8, stack4: 32, stack5: 20
      },
      {
        display: "Extremely Long Category Name That Should Definitely Test the Limits of Text Display and Legend Positioning",
        stack1: 5, stack2: 15, stack3: 25, stack4: 35, stack5: 20
      },
      {
        display: "C",
        stack1: 7, stack2: 13, stack3: 27, stack4: 33, stack5: 20
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types",
    xAxisLabel: "Device Types",
    yAxisLabel: "Categories"
  },

  // Single data point
  singleDataPoint: {
    name: "Single Data Point",
    description: "Single data point for testing edge cases",
    data: [
      {
        display: "Only Category",
        stack1: 100, stack2: 50, stack3: 25, stack4: 75, stack5: 10
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types",
    xAxisLabel: "Device Types",
    yAxisLabel: "Single Category"
  },

  // Large dataset (50+ items) for performance testing
  largeDataset: {
    name: "Large Dataset (50+ items)",
    description: "Large dataset for testing performance and rendering",
    data: Array.from({ length: 50 }, (_, i) => ({
      display: `Category ${i + 1}`,
      stack1: Math.floor(Math.random() * 100) + 1,
      stack2: Math.floor(Math.random() * 100) + 1,
      stack3: Math.floor(Math.random() * 100) + 1,
      stack4: Math.floor(Math.random() * 100) + 1,
      stack5: Math.floor(Math.random() * 100) + 1
    })),
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types",
    xAxisLabel: "Device Types",
    yAxisLabel: "Categories (Large Dataset)"
  },

  // Very short labels
  shortLabels: {
    name: "Short Labels",
    description: "Dataset with very short category names",
    data: [
      {
        display: "A",
        stack1: 30, stack2: 25, stack3: 20, stack4: 15, stack5: 10
      },
      {
        display: "B",
        stack1: 25, stack2: 30, stack3: 15, stack4: 20, stack5: 10
      },
      {
        display: "C",
        stack1: 20, stack2: 15, stack3: 30, stack4: 25, stack5: 10
      },
      {
        display: "D",
        stack1: 15, stack2: 20, stack3: 25, stack4: 30, stack5: 10
      },
      {
        display: "E",
        stack1: 10, stack2: 10, stack3: 10, stack4: 10, stack5: 60
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types",
    xAxisLabel: "Device Types",
    yAxisLabel: "Short Categories"
  },

  // Very long labels
  longLabels: {
    name: "Long Labels",
    description: "Dataset with very long category names",
    data: [
      {
        display: "Users Who Completed the Entire Onboarding Process Including Email Verification and Profile Setup",
        stack1: 60, stack2: 30, stack3: 10, stack4: 0, stack5: 0
      },
      {
        display: "Users Who Made Their First Purchase Within 24 Hours of Registration and Left a Review",
        stack1: 35, stack2: 45, stack3: 15, stack4: 5, stack5: 0
      },
      {
        display: "Power Users Who Created More Than 10 Posts and Engaged with Community Features Daily",
        stack1: 15, stack2: 25, stack3: 35, stack4: 20, stack5: 5
      },
      {
        display: "Premium Subscribers Who Upgraded Within the First Week and Enabled All Notification Settings",
        stack1: 8, stack2: 12, stack3: 18, stack4: 42, stack5: 20
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types",
    xAxisLabel: "Device Types",
    yAxisLabel: "Long Categories"
  },

  // Mixed label lengths
  mixedLabels: {
    name: "Mixed Label Lengths",
    description: "Mix of short, medium, and long labels",
    data: [
      {
        display: "A",
        stack1: 20, stack2: 30, stack3: 25, stack4: 15, stack5: 10
      },
      {
        display: "Short Label",
        stack1: 15, stack2: 25, stack3: 30, stack4: 20, stack5: 10
      },
      {
        display: "Medium Length Category Name",
        stack1: 25, stack2: 20, stack3: 15, stack4: 30, stack5: 10
      },
      {
        display: "Very Long Category Name That Tests Text Wrapping and Legend Display",
        stack1: 18, stack2: 22, stack3: 28, stack4: 20, stack5: 12
      },
      {
        display: "B",
        stack1: 12, stack2: 18, stack3: 22, stack4: 28, stack5: 20
      },
      {
        display: "Another Medium Category",
        stack1: 10, stack2: 15, stack3: 20, stack4: 25, stack5: 30
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types",
    xAxisLabel: "Device Types",
    yAxisLabel: "Mixed Length Categories"
  },

  // Very small values for precision testing
  smallValues: {
    name: "Small Values",
    description: "Dataset with very small values to test precision",
    data: [
      {
        display: "Tiny Value",
        stack1: 0.1, stack2: 0.2, stack3: 0.3, stack4: 0.4, stack5: 0.0
      },
      {
        display: "Small Value",
        stack1: 0.5, stack2: 1.0, stack3: 1.5, stack4: 2.0, stack5: 0.0
      },
      {
        display: "Medium Value",
        stack1: 1.2, stack2: 2.4, stack3: 3.6, stack4: 4.8, stack5: 0.0
      },
      {
        display: "Large Value",
        stack1: 98.2, stack2: 1.0, stack3: 0.5, stack4: 0.2, stack5: 0.1
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types",
    xAxisLabel: "Device Types",
    yAxisLabel: "Small Values"
  },

  // Zero and negative values
  edgeValues: {
    name: "Edge Values",
    description: "Dataset with zero and negative values",
    data: [
      {
        display: "Zero Value",
        stack1: 0, stack2: 0, stack3: 0, stack4: 0, stack5: 0
      },
      {
        display: "Negative Value",
        stack1: -5, stack2: -10, stack3: -15, stack4: -20, stack5: -25
      },
      {
        display: "Positive Value",
        stack1: 105, stack2: 95, stack3: 85, stack4: 75, stack5: 65
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types",
    xAxisLabel: "Device Types",
    yAxisLabel: "Edge Values"
  },

  // Sparse data with some zero values
  sparseData: {
    name: "Sparse Data",
    description: "Dataset with some zero/missing values",
    data: [
      {
        display: "Category A",
        stack1: 100, stack2: 0, stack3: 50, stack4: 0, stack5: 25
      },
      {
        display: "Category B",
        stack1: 0, stack2: 80, stack3: 0, stack4: 60, stack5: 0
      },
      {
        display: "Category C",
        stack1: 30, stack2: 40, stack3: 0, stack4: 0, stack5: 70
      },
      {
        display: "Category D",
        stack1: 0, stack2: 0, stack3: 90, stack4: 0, stack5: 0
      },
      {
        display: "Category E",
        stack1: 70, stack2: 0, stack3: 0, stack4: 0, stack5: 30
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types",
    xAxisLabel: "Device Types",
    yAxisLabel: "Sparse Data"
  },

  // Multi-stack data with many dimensions
  multiStack: {
    name: "Multi-Stack Data",
    description: "Data with many stack dimensions for comprehensive testing",
    data: [
      {
        display: "Logged In Once",
        stack1: 45, stack2: 23, stack3: 67, stack4: 12, stack5: 89,
        stack6: 34, stack7: 56, stack8: 78, stack9: 21, stack10: 43
      },
      {
        display: "Registered Users",
        stack1: 92, stack2: 15, stack3: 71, stack4: 25, stack5: 84,
        stack6: 47, stack7: 69, stack8: 13, stack9: 56, stack10: 91
      },
      {
        display: "Created Profile",
        stack1: 33, stack2: 77, stack3: 14, stack4: 59, stack5: 86,
        stack6: 31, stack7: 68, stack8: 42, stack9: 95, stack10: 26
      },
      {
        display: "Wrote at least 3 story",
        stack1: 18, stack2: 52, stack3: 79, stack4: 35, stack5: 61,
        stack6: 93, stack7: 24, stack8: 67, stack9: 41, stack10: 88
      },
      {
        display: "Created Profile again",
        stack1: 44, stack2: 87, stack3: 21, stack4: 63, stack5: 96,
        stack6: 38, stack7: 72, stack8: 55, stack9: 19, stack10: 81
      },
      {
        display: "Wrote at least 1 story",
        stack1: 57, stack2: 23, stack3: 89, stack4: 41, stack5: 76,
        stack6: 12, stack7: 64, stack8: 98, stack9: 33, stack10: 78
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5", "stack6", "stack7", "stack8", "stack9", "stack10"],
    tooltipLabelsMapping: {
      stack1: 'Q1 2024', stack2: 'Q2 2024', stack3: 'Q3 2024', stack4: 'Q4 2024', stack5: 'Q1 2025',
      stack6: 'Q2 2025', stack7: 'Q3 2025', stack8: 'Q4 2025', stack9: 'Q1 2026', stack10: 'Q2 2026'
    },
    labelPath: "display",
    breakdownCategoryName: "Quarters"
  },

  // Extreme edge cases
  extremeCases: {
    name: "Extreme Edge Cases",
    description: "Extreme edge cases for testing",
    data: [
      {
        display: "A",
        stack1: 0.001, stack2: 0.002, stack3: 0.003, stack4: 0.004, stack5: 0.005
      },
      {
        display: "Very Long Category Name That Should Test Text Wrapping and Overflow Handling in HeatMap Grid Legends and Tooltips",
        stack1: 0.002, stack2: 0.004, stack3: 0.006, stack4: 0.008, stack5: 0.010
      },
      {
        display: "B",
        stack1: 99.997, stack2: 99.996, stack3: 99.995, stack4: 99.994, stack5: 99.993
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types"
  },

  // Real-world scenario data
  realWorld: {
    name: "Real World Scenario",
    description: "Real-world data scenario for heatmap",
    data: [
      {
        display: "Desktop Users",
        stack1: 45, stack2: 35, stack3: 15, stack4: 5, stack5: 0
      },
      {
        display: "Mobile Users",
        stack1: 25, stack2: 50, stack3: 20, stack4: 5, stack5: 0
      },
      {
        display: "Tablet Users",
        stack1: 15, stack2: 25, stack3: 45, stack4: 10, stack5: 5
      },
      {
        display: "Other Devices",
        stack1: 5, stack2: 10, stack3: 15, stack4: 60, stack5: 10
      },
      {
        display: "Unknown Devices",
        stack1: 10, stack2: 5, stack3: 5, stack4: 20, stack5: 60
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'North America',
      stack2: 'Europe',
      stack3: 'Asia',
      stack4: 'Other Regions',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Regions"
  },

  // Demographics heatmap data
  demographics: {
    name: "Demographics Heatmap",
    description: "Age group vs device type heatmap data",
    data: [
      {
        display: "18-25 Years",
        stack1: 70, stack2: 20, stack3: 8, stack4: 2, stack5: 0
      },
      {
        display: "26-35 Years",
        stack1: 60, stack2: 30, stack3: 8, stack4: 2, stack5: 0
      },
      {
        display: "36-45 Years",
        stack1: 50, stack2: 35, stack3: 12, stack4: 3, stack5: 0
      },
      {
        display: "46-55 Years",
        stack1: 40, stack2: 40, stack3: 15, stack4: 5, stack5: 0
      },
      {
        display: "56-65 Years",
        stack1: 30, stack2: 35, stack3: 20, stack4: 10, stack5: 5
      },
      {
        display: "65+ Years",
        stack1: 25, stack2: 30, stack3: 25, stack4: 15, stack5: 5
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Desktop',
      stack2: 'Mobile',
      stack3: 'Tablet',
      stack4: 'Other',
      stack5: 'Unknown'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types"
  },

  // Time-based heatmap data
  timeBased: {
    name: "Time-Based Heatmap",
    description: "Hour vs day of week heatmap data",
    data: [
      {
        display: "Monday",
        stack1: 20, stack2: 30, stack3: 40, stack4: 50, stack5: 60,
        stack6: 70, stack7: 80, stack8: 90, stack9: 85, stack10: 75,
        stack11: 65, stack12: 55, stack13: 45, stack14: 35, stack15: 25,
        stack16: 20, stack17: 25, stack18: 30, stack19: 35, stack20: 40,
        stack21: 35, stack22: 30, stack23: 25, stack24: 20
      },
      {
        display: "Tuesday",
        stack1: 25, stack2: 35, stack3: 45, stack4: 55, stack5: 65,
        stack6: 75, stack7: 85, stack8: 95, stack9: 90, stack10: 80,
        stack11: 70, stack12: 60, stack13: 50, stack14: 40, stack15: 30,
        stack16: 25, stack17: 30, stack18: 35, stack19: 40, stack20: 45,
        stack21: 40, stack22: 35, stack23: 30, stack24: 25
      },
      {
        display: "Wednesday",
        stack1: 30, stack2: 40, stack3: 50, stack4: 60, stack5: 70,
        stack6: 80, stack7: 90, stack8: 100, stack9: 95, stack10: 85,
        stack11: 75, stack12: 65, stack13: 55, stack14: 45, stack15: 35,
        stack16: 30, stack17: 35, stack18: 40, stack19: 45, stack20: 50,
        stack21: 45, stack22: 40, stack23: 35, stack24: 30
      },
      {
        display: "Thursday",
        stack1: 35, stack2: 45, stack3: 55, stack4: 65, stack5: 75,
        stack6: 85, stack7: 95, stack8: 105, stack9: 100, stack10: 90,
        stack11: 80, stack12: 70, stack13: 60, stack14: 50, stack15: 40,
        stack16: 35, stack17: 40, stack18: 45, stack19: 50, stack20: 55,
        stack21: 50, stack22: 45, stack23: 40, stack24: 35
      },
      {
        display: "Friday",
        stack1: 40, stack2: 50, stack3: 60, stack4: 70, stack5: 80,
        stack6: 90, stack7: 100, stack8: 110, stack9: 105, stack10: 95,
        stack11: 85, stack12: 75, stack13: 65, stack14: 55, stack15: 45,
        stack16: 40, stack17: 45, stack18: 50, stack19: 55, stack20: 60,
        stack21: 55, stack22: 50, stack23: 45, stack24: 40
      },
      {
        display: "Saturday",
        stack1: 15, stack2: 20, stack3: 25, stack4: 30, stack5: 35,
        stack6: 40, stack7: 45, stack8: 50, stack9: 55, stack10: 60,
        stack11: 65, stack12: 70, stack13: 75, stack14: 80, stack15: 85,
        stack16: 80, stack17: 75, stack18: 70, stack19: 65, stack20: 60,
        stack21: 55, stack22: 50, stack23: 45, stack24: 40
      },
      {
        display: "Sunday",
        stack1: 10, stack2: 15, stack3: 20, stack4: 25, stack5: 30,
        stack6: 35, stack7: 40, stack8: 45, stack9: 50, stack10: 55,
        stack11: 60, stack12: 65, stack13: 70, stack14: 75, stack15: 80,
        stack16: 75, stack17: 70, stack18: 65, stack19: 60, stack20: 55,
        stack21: 50, stack22: 45, stack23: 40, stack24: 35
      }
    ],
    valuePath: [
      "stack1", "stack2", "stack3", "stack4", "stack5", "stack6", "stack7", "stack8", "stack9", "stack10",
      "stack11", "stack12", "stack13", "stack14", "stack15", "stack16", "stack17", "stack18", "stack19", "stack20",
      "stack21", "stack22", "stack23", "stack24"
    ],
    tooltipLabelsMapping: {
      stack1: '12 AM', stack2: '1 AM', stack3: '2 AM', stack4: '3 AM', stack5: '4 AM', stack6: '5 AM',
      stack7: '6 AM', stack8: '7 AM', stack9: '8 AM', stack10: '9 AM', stack11: '10 AM', stack12: '11 AM',
      stack13: '12 PM', stack14: '1 PM', stack15: '2 PM', stack16: '3 PM', stack17: '4 PM', stack18: '5 PM',
      stack19: '6 PM', stack20: '7 PM', stack21: '8 PM', stack22: '9 PM', stack23: '10 PM', stack24: '11 PM'
    },
    labelPath: "display",
    breakdownCategoryName: "Hours"
  },

  // Enhanced test cases with labels and sublabels
  withLabelsAndSublabels: {
    name: "With Labels and Sublabels",
    description: "Data with main labels and sublabels for comprehensive testing",
    data: [
      {
        display: "North America",
        sublabel: "United States",
        stack1: 45, stack2: 30, stack3: 25, stack4: 20, stack5: 15
      },
      {
        display: "North America",
        sublabel: "Canada",
        stack1: 35, stack2: 25, stack3: 20, stack4: 15, stack5: 10
      },
      {
        display: "North America",
        sublabel: "Mexico",
        stack1: 25, stack2: 20, stack3: 15, stack4: 10, stack5: 5
      },
      {
        display: "Europe",
        sublabel: "United Kingdom",
        stack1: 40, stack2: 35, stack3: 30, stack4: 25, stack5: 20
      },
      {
        display: "Europe",
        sublabel: "Germany",
        stack1: 50, stack2: 40, stack3: 35, stack4: 30, stack5: 25
      },
      {
        display: "Europe",
        sublabel: "France",
        stack1: 30, stack2: 25, stack3: 20, stack4: 15, stack5: 10
      },
      {
        display: "Asia",
        sublabel: "China",
        stack1: 60, stack2: 50, stack3: 40, stack4: 30, stack5: 20
      },
      {
        display: "Asia",
        sublabel: "Japan",
        stack1: 35, stack2: 30, stack3: 25, stack4: 20, stack5: 15
      },
      {
        display: "Asia",
        sublabel: "India",
        stack1: 55, stack2: 45, stack3: 35, stack4: 25, stack5: 15
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Q1 2024',
      stack2: 'Q2 2024',
      stack3: 'Q3 2024',
      stack4: 'Q4 2024',
      stack5: 'Q1 2025'
    },
    labelPath: "display",
    breakdownCategoryName: "Quarters",
    xAxisLabel: "Quarters",
    yAxisLabel: "Regions & Countries"
  },

  // Massive dataset with many cells and columns
  massiveDataset: {
    name: "Massive Dataset (100+ cells)",
    description: "Large dataset with many rows and columns for stress testing",
    data: Array.from({ length: 20 }, (_, i) => {
      const row = {
        display: `Category ${String(i + 1).padStart(2, '0')}`,
        sublabel: `Subcategory ${String(i + 1).padStart(2, '0')}`
      };
      
      // Add 15 columns of data
      for (let j = 1; j <= 15; j++) {
        row[`stack${j}`] = Math.floor(Math.random() * 100) + 1;
      }
      
      return row;
    }),
    valuePath: Array.from({ length: 15 }, (_, i) => `stack${i + 1}`),
    tooltipLabelsMapping: Array.from({ length: 15 }, (_, i) => ({
      [`stack${i + 1}`]: `Column ${String(i + 1).padStart(2, '0')}`
    })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
    labelPath: "display",
    breakdownCategoryName: "Columns",
    xAxisLabel: "Columns (15)",
    yAxisLabel: "Categories (20)"
  },

  // Mixed label sizes - small and large combinations
  mixedLabelSizes: {
    name: "Mixed Label Sizes",
    description: "Combination of very small and very large labels",
    data: [
      {
        display: "A",
        stack1: 10, stack2: 20, stack3: 30, stack4: 40, stack5: 50
      },
      {
        display: "Very Long Category Name That Should Test Text Wrapping and Overflow Handling in HeatMap Grid Legends and Tooltips and Should Definitely Exceed Normal Display Limits",
        stack1: 15, stack2: 25, stack3: 35, stack4: 45, stack5: 55
      },
      {
        display: "B",
        stack1: 20, stack2: 30, stack3: 40, stack4: 50, stack5: 60
      },
      {
        display: "Medium Length Category Name",
        stack1: 25, stack2: 35, stack3: 45, stack4: 55, stack5: 65
      },
      {
        display: "C",
        stack1: 30, stack2: 40, stack3: 50, stack4: 60, stack5: 70
      },
      {
        display: "Another Extremely Long Category Name That Should Test the Limits of Text Display and Legend Positioning and UI Responsiveness in Various Screen Sizes",
        stack1: 35, stack2: 45, stack3: 55, stack4: 65, stack5: 75
      },
      {
        display: "D",
        stack1: 40, stack2: 50, stack3: 60, stack4: 70, stack5: 80
      },
      {
        display: "Short",
        stack1: 45, stack2: 55, stack3: 65, stack4: 75, stack5: 85
      }
    ],
    valuePath: ["stack1", "stack2", "stack3", "stack4", "stack5"],
    tooltipLabelsMapping: {
      stack1: 'Very Long Column Header Name That Tests Text Wrapping',
      stack2: 'B',
      stack3: 'Medium Length Column Header',
      stack4: 'D',
      stack5: 'Another Very Long Column Header Name That Should Test Text Wrapping and Overflow Handling'
    },
    labelPath: "display",
    breakdownCategoryName: "Mixed Sizes",
    xAxisLabel: "Mixed Size Columns",
    yAxisLabel: "Mixed Size Categories"
  },

  // Many columns with small labels
  manyColumnsSmallLabels: {
    name: "Many Columns - Small Labels",
    description: "Many columns with short, concise labels",
    data: [
      {
        display: "A", stack1: 10, stack2: 20, stack3: 30, stack4: 40, stack5: 50,
        stack6: 60, stack7: 70, stack8: 80, stack9: 90, stack10: 100,
        stack11: 15, stack12: 25, stack13: 35, stack14: 45, stack15: 55
      },
      {
        display: "B", stack1: 15, stack2: 25, stack3: 35, stack4: 45, stack5: 55,
        stack6: 65, stack7: 75, stack8: 85, stack9: 95, stack10: 105,
        stack11: 20, stack12: 30, stack13: 40, stack14: 50, stack15: 60
      },
      {
        display: "C", stack1: 20, stack2: 30, stack3: 40, stack4: 50, stack5: 60,
        stack6: 70, stack7: 80, stack8: 90, stack9: 100, stack10: 110,
        stack11: 25, stack12: 35, stack13: 45, stack14: 55, stack15: 65
      },
      {
        display: "D", stack1: 25, stack2: 35, stack3: 45, stack4: 55, stack5: 65,
        stack6: 75, stack7: 85, stack8: 95, stack9: 105, stack10: 115,
        stack11: 30, stack12: 40, stack13: 50, stack14: 60, stack15: 70
      },
      {
        display: "E", stack1: 30, stack2: 40, stack3: 50, stack4: 60, stack5: 70,
        stack6: 80, stack7: 90, stack8: 100, stack9: 110, stack10: 120,
        stack11: 35, stack12: 45, stack13: 55, stack14: 65, stack15: 75
      }
    ],
    valuePath: Array.from({ length: 15 }, (_, i) => `stack${i + 1}`),
    tooltipLabelsMapping: Array.from({ length: 15 }, (_, i) => ({
      [`stack${i + 1}`]: `C${String(i + 1).padStart(2, '0')}`
    })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
    labelPath: "display",
    breakdownCategoryName: "Columns",
    xAxisLabel: "Small Column Labels",
    yAxisLabel: "Small Category Labels"
  },

  // Many columns with large labels
  manyColumnsLargeLabels: {
    name: "Many Columns - Large Labels",
    description: "Many columns with long, descriptive labels",
    data: [
      {
        display: "Very Long Row Label That Should Test Text Wrapping and Overflow Handling in HeatMap Grid Legends",
        stack1: 10, stack2: 20, stack3: 30, stack4: 40, stack5: 50,
        stack6: 60, stack7: 70, stack8: 80, stack9: 90, stack10: 100,
        stack11: 15, stack12: 25, stack13: 35, stack14: 45, stack15: 55
      },
      {
        display: "Another Extremely Long Row Label That Should Test the Limits of Text Display and Legend Positioning",
        stack1: 15, stack2: 25, stack3: 35, stack4: 45, stack5: 55,
        stack6: 65, stack7: 75, stack8: 85, stack9: 95, stack10: 105,
        stack11: 20, stack12: 30, stack13: 40, stack14: 50, stack15: 60
      },
      {
        display: "Third Very Long Row Label That Should Test UI Responsiveness and Text Handling in Various Screen Sizes",
        stack1: 20, stack2: 30, stack3: 40, stack4: 50, stack5: 60,
        stack6: 70, stack7: 80, stack8: 90, stack9: 100, stack10: 110,
        stack11: 25, stack12: 35, stack13: 45, stack14: 55, stack15: 65
      }
    ],
    valuePath: Array.from({ length: 15 }, (_, i) => `stack${i + 1}`),
    tooltipLabelsMapping: Array.from({ length: 15 }, (_, i) => ({
      [`stack${i + 1}`]: `Very Long Column Header Name ${i + 1} That Should Test Text Wrapping and Overflow Handling in HeatMap Grid Legends`
    })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
    labelPath: "display",
    breakdownCategoryName: "Long Columns",
    xAxisLabel: "Very Long Column Headers",
    yAxisLabel: "Very Long Row Labels"
  },

  // Extreme edge case with maximum cells
  extremeMaxCells: {
    name: "Extreme Max Cells (500+ cells)",
    description: "Maximum stress test with 25 rows x 20 columns = 500 cells",
    data: Array.from({ length: 25 }, (_, i) => {
      const row = {
        display: `Row ${String(i + 1).padStart(2, '0')}`
      };
      
      // Add 20 columns of data
      for (let j = 1; j <= 20; j++) {
        row[`stack${j}`] = Math.floor(Math.random() * 100) + 1;
      }
      
      return row;
    }),
    valuePath: Array.from({ length: 20 }, (_, i) => `stack${i + 1}`),
    tooltipLabelsMapping: Array.from({ length: 20 }, (_, i) => ({
      [`stack${i + 1}`]: `Col ${String(i + 1).padStart(2, '0')}`
    })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
    labelPath: "display",
    breakdownCategoryName: "Columns",
    xAxisLabel: "Columns (20)",
    yAxisLabel: "Rows (25)"
  },

  // Mixed combinations - some small, some large labels
  mixedCombinations: {
    name: "Mixed Combinations",
    description: "Mix of small and large labels in both rows and columns",
    data: [
      {
        display: "A",
        stack1: 10, stack2: 20, stack3: 30, stack4: 40, stack5: 50,
        stack6: 60, stack7: 70, stack8: 80, stack9: 90, stack10: 100
      },
      {
        display: "Very Long Category Name That Should Test Text Wrapping and Overflow Handling",
        stack1: 15, stack2: 25, stack3: 35, stack4: 45, stack5: 55,
        stack6: 65, stack7: 75, stack8: 85, stack9: 95, stack10: 105
      },
      {
        display: "B",
        stack1: 20, stack2: 30, stack3: 40, stack4: 50, stack5: 60,
        stack6: 70, stack7: 80, stack8: 90, stack9: 100, stack10: 110
      },
      {
        display: "Medium Length Category Name",
        stack1: 25, stack2: 35, stack3: 45, stack4: 55, stack5: 65,
        stack6: 75, stack7: 85, stack8: 95, stack9: 105, stack10: 115
      },
      {
        display: "C",
        stack1: 30, stack2: 40, stack3: 50, stack4: 60, stack5: 70,
        stack6: 80, stack7: 90, stack8: 100, stack9: 110, stack10: 120
      },
      {
        display: "Another Extremely Long Category Name That Should Test the Limits of Text Display",
        stack1: 35, stack2: 45, stack3: 55, stack4: 65, stack5: 75,
        stack6: 85, stack7: 95, stack8: 105, stack9: 115, stack10: 125
      }
    ],
    valuePath: Array.from({ length: 10 }, (_, i) => `stack${i + 1}`),
    tooltipLabelsMapping: {
      stack1: 'A',
      stack2: 'Very Long Column Header Name That Should Test Text Wrapping',
      stack3: 'B',
      stack4: 'Medium Length Column Header',
      stack5: 'C',
      stack6: 'Another Very Long Column Header Name That Should Test Text Wrapping and Overflow Handling',
      stack7: 'D',
      stack8: 'Short',
      stack9: 'E',
      stack10: 'Very Long Column Header Name That Should Test Text Wrapping and Overflow Handling in HeatMap Grid Legends'
    },
    labelPath: "display",
    breakdownCategoryName: "Mixed Sizes",
    xAxisLabel: "Mixed Column Labels",
    yAxisLabel: "Mixed Row Labels"
  }
};

// Helper function to get all test data names
export const getHeatMapGridTestDataNames = () => {
  return Object.keys(heatMapGridTestData);
};

// Helper function to get specific test data
export const getHeatMapGridTestData = (name) => {
  return heatMapGridTestData[name] || null;
};
